import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { metaFor } from "./cityMeta";
import { MatchBadge } from "./MatchBadge";
import { ShareCity } from "./ShareCity";
import { ROTATE_MS, prefersReducedMotion, useRotation } from "./useRotation";
import { CtaTile, HeroTile, IndustryTile, KpiTile, LivingTile, StudyTile, VisaQuoteTile } from "./CityBlocks";

const SHORT = { "new-york": "New York" };

function CityNav({ cities, index, go, paused, cycle }) {
  const listRef = useRef(null);
  useEffect(() => {
    const list = listRef.current;
    const tab = list?.children[index];
    if (tab) list.scrollLeft = tab.offsetLeft - list.clientWidth / 2 + tab.clientWidth / 2;
  }, [index]);
  return (
    <div className="flex items-center gap-2 sm:gap-3 pb-4 mb-6 border-b border-stone-200" data-testid="featured-cities-nav-bar">
      <div ref={listRef} className="relative flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar min-w-0 flex-1 scroll-smooth" role="tablist">
        {cities.map((c, i) => {
          const accent = metaFor(c).accent;
          const active = i === index;
          return (
            <button key={c.slug} role="tab" aria-selected={active} onClick={() => go(i)}
              className={`relative shrink-0 px-3 py-2 text-sm font-medium rounded-md transition-colors ${active ? "text-stone-900 bg-white border border-stone-200" : "text-stone-500 hover:text-stone-900"}`}
              data-testid={`featured-cities-city-button-${c.slug}`}>
              {SHORT[c.slug] || c.name}
              {active && (
                <span className="absolute left-2 right-2 bottom-0.5 h-0.5 bg-stone-200 overflow-hidden rounded">
                  <span key={cycle} className="block h-full featured-city-progress" style={{ background: accent, animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? "paused" : "running" }} data-testid={`featured-cities-${i}-progress-bar`} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <button onClick={() => go(index - 1)} aria-label="Previous city" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-cities-prev-button"><ChevronLeft className="h-4 w-4" /></button>
      <button onClick={() => go(index + 1)} aria-label="Next city" className="p-2 rounded-full border border-stone-300 hover:bg-white transition-colors" data-testid="featured-cities-next-button"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

function CitySpread({ c, slugs, match, onRefresh }) {
  const accent = metaFor(c).accent;
  const badge = <MatchBadge match={match?.cities?.[c.slug]} prefs={match?.lifestyle_prefs || []} cityName={c.name} onRefresh={onRefresh} />;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
      <HeroTile c={c} badge={badge} />
      <KpiTile c={c} />
      <StudyTile c={c} accent={accent} />
      <LivingTile c={c} accent={accent} />
      <IndustryTile c={c} accent={accent} />
      <VisaQuoteTile c={c} accent={accent} />
      <CtaTile c={c} slugs={slugs} share={<ShareCity c={c} />} />
    </div>
  );
}

export default function FeaturedCitiesPromo() {
  const [cities, setCities] = useState([]);
  const [match, setMatch] = useState(null);
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sectionRef = useRef(null);
  const deepLink = params.get("city");
  const initial = useMemo(() => Math.max(0, cities.findIndex((x) => x.slug === deepLink)), [cities, deepLink]);
  const { index, paused, setPaused, go, cycle } = useRotation(cities.length, initial);
  const reduced = prefersReducedMotion();
  const slugs = cities.map((x) => x.slug);

  useEffect(() => {
    api.get("/cities/featured").then(({ data }) => setCities(data.cities || [])).catch(() => setCities([]));
  }, []);
  const loadMatch = useCallback(() => {
    if (!user) { setMatch(null); return; }
    api.get("/featured-cities/match").then(({ data }) => setMatch(data)).catch(() => setMatch(null));
  }, [user]);
  useEffect(() => { loadMatch(); }, [loadMatch]);
  useEffect(() => {
    if (deepLink && cities.length && sectionRef.current) sectionRef.current.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });
  }, [deepLink, cities.length, reduced]);

  if (!cities.length) return null;
  const c = cities[index];
  const onKey = (e) => {
    if (e.key === "ArrowRight") go(index + 1);
    if (e.key === "ArrowLeft") go(index - 1);
  };

  return (
    <section ref={sectionRef} id="featured-cities" className="border-b border-stone-200 bg-[#F6F3EC] overflow-x-hidden scroll-mt-40" data-testid="featured-cities-promo-section"
      tabIndex={0} onKeyDown={onKey} aria-roledescription="carousel" aria-label="Featured cities"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-6">
          <div>
            <p className="font-data text-xs uppercase tracking-widest text-stone-500">Featured city</p>
            <h2 className="font-editorial text-3xl sm:text-4xl text-stone-900">Study, live and work in a global hub</h2>
          </div>
          <p className="text-sm text-stone-500 sm:text-right max-w-sm">Every number comes from our university, course, living-cost and employer data.</p>
        </div>
        <CityNav cities={cities} index={index} go={go} paused={paused} cycle={cycle} />
        <AnimatePresence mode="wait">
          <motion.div key={c.slug} initial={reduced ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 1 } : { opacity: 0, y: -12 }} transition={{ duration: reduced ? 0 : 0.35, ease: "easeOut" }}
            aria-live="polite" data-testid={`featured-cities-spread-${c.slug}`}>
            <CitySpread c={c} slugs={slugs} match={match} onRefresh={loadMatch} />
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
